import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  CacheExpiryConstants,
  CacheKeyConstants,
  CacheService,
  DateFormatConstants,
  isNullOrUndefined,
  PaymentAccountStatusConstants
} from '@uzurv/core';
// eslint-disable-next-line import/order
import { UzurvRestConfiguration, UzurvRestService, UZURV_REST_CONFIGURATION } from '@uzurv/http';
import { Deserialize } from 'cerialize';
import moment from 'moment-timezone';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CardType, Payment, PaymentAccount, PaymentMethod, State } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class PaymentAccountService extends UzurvRestService {
  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer,
    protected cacheService: CacheService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Returns the available payment methods, overriding locally cached values if passed true
   *
   * @param overrideCache
   */
  getPaymentMethods(overrideCache = false): Observable<Array<PaymentMethod>> {
    const cachedData = this.cacheService.getCachedData(CacheKeyConstants.PAYMENT_METHODS);
    if (!overrideCache && !isNullOrUndefined(cachedData)) {
      return of(Deserialize(JSON.parse(cachedData), PaymentMethod));
    }

    return this.get('paymentAccounts/paymentMethods/', {
      pageNumber: 0,
      pageSize: 100
    }).pipe(
      map(response => {
        this.cacheService.cacheData(CacheKeyConstants.PAYMENT_METHODS, JSON.stringify(response), CacheExpiryConstants.DOMAIN_CACHE_EXPIRY);
        return Deserialize(response, PaymentMethod);
      })
    );
  }

  /**
   * Returns a payment account by ID
   *
   * @param id
   * @param overrideCache
   */
  findPaymentMethodById(id: number, overrideCache = false): Observable<PaymentMethod> {
    return this.getPaymentMethods(overrideCache).pipe(map(response => response.find(paymentMethod => paymentMethod.id === id)));
  }

  /**
   * Returns a payment account by name
   *
   * @param name
   * @param overrideCache
   */
  findPaymentMethodByName(name: string, overrideCache = false): Observable<PaymentMethod> {
    return this.getPaymentMethods(overrideCache).pipe(map(response => response.find(paymentMethod => paymentMethod.name === name)));
  }

  /**
   * Returns a list of available credit card types
   *
   * @param overrideCache
   */
  getCardTypes(overrideCache = false): Observable<Array<CardType>> {
    const cachedData = this.cacheService.getCachedData(CacheKeyConstants.CARD_TYPES);
    if (!overrideCache && !isNullOrUndefined(cachedData)) {
      return of(Deserialize(JSON.parse(cachedData), State));
    }

    return this.get('paymentAccounts/cardTypes/', {
      pageNumber: 0,
      pageSize: 100
    }).pipe(
      map(response => {
        this.cacheService.cacheData(CacheKeyConstants.CARD_TYPES, JSON.stringify(response), CacheExpiryConstants.DOMAIN_CACHE_EXPIRY);
        return Deserialize(response, CardType);
      })
    );
  }

  /**
   * Returns a card type by ID
   *
   * @param id
   * @param overrideCache
   */
  findCardTypeById(id: number, overrideCache = false): Observable<CardType> {
    return this.getCardTypes(overrideCache).pipe(map(response => response.find(cardType => cardType.id === id)));
  }

  /**
   * Returns a card type by name
   *
   * @param name
   * @param overrideCache
   */
  findCardTypeByName(name: string, overrideCache = false): Observable<CardType> {
    return this.getCardTypes(overrideCache).pipe(map(response => response.find(cardType => cardType.name === name)));
  }

  /**
   * Returns a payment by ID
   *
   * @param paymentId
   */
  findPaymentById(paymentId: number): Observable<Payment> {
    return this.get('payments/' + paymentId).pipe(map(response => Deserialize(response, Payment)));
  }

  // Utils

  filterPaymentAccountsByPaymentMethod(paymentAccounts: Array<PaymentAccount>, paymentMethodName: string): Array<PaymentAccount> {
    if (isNullOrUndefined(paymentAccounts)) {
      return [];
    }

    return paymentAccounts.filter(pa => pa.paymentMethod.name === paymentMethodName);
  }

  // Errors
  sortPaymentAccountsByChargeOrder(paymentAccounts: Array<PaymentAccount>): Array<PaymentAccount> {
    return paymentAccounts.sort((a, b) => a.chargeOrder - b.chargeOrder);
  }

  /**
   * Returns the first valid payment account from an array sorted by charge order.
   *
   * @param paymentAccounts
   */
  getFirstValidPaymentAccount(paymentAccounts: Array<PaymentAccount>) {
    if (isNullOrUndefined(paymentAccounts)) {
      return null;
    }

    paymentAccounts = this.sortPaymentAccountsByChargeOrder(paymentAccounts);

    let validPaymentAccount;
    for (const paymentAccount of paymentAccounts) {
      if (!this.doesPaymentAccountHaveErrors(paymentAccount)) {
        validPaymentAccount = paymentAccount;
        break;
      }
    }

    return validPaymentAccount;
  }

  doesPaymentAccountHaveErrors(paymentAccount: PaymentAccount, onDate: Date = new Date()): boolean {
    return (
      paymentAccount.paymentAccountStatus.name === PaymentAccountStatusConstants.EXPIRED ||
      this.isPaymentAccountExpired(paymentAccount, onDate) ||
      paymentAccount.paymentAccountStatus.name === PaymentAccountStatusConstants.PROCESSOR_REJECTED ||
      paymentAccount.paymentAccountStatus.name === PaymentAccountStatusConstants.PROCESSOR_DELETED
    );
  }

  getPaymentAccountErrorMessage(paymentAccount: PaymentAccount, onDate: Date = new Date()): Array<string> {
    const messagesArr = [];

    if (
      this.isPaymentAccountExpired(paymentAccount, onDate) ||
      paymentAccount.paymentAccountStatus.name === PaymentAccountStatusConstants.EXPIRED
    ) {
      messagesArr.push('Payment Account Error Expired');
    }

    if (paymentAccount.paymentAccountStatus.name === PaymentAccountStatusConstants.PROCESSOR_REJECTED) {
      messagesArr.push('Payment Account Error Processor Rejected');
    }

    if (paymentAccount.paymentAccountStatus.name === PaymentAccountStatusConstants.PROCESSOR_DELETED) {
      messagesArr.push('Payment Account Error Processor Rejected');
    }

    return messagesArr;
  }

  /**
   * Gets a formatted expiration date for display
   *
   * @param expirationDate
   */
  getFormattedExpirationDate(expirationDate: string) {
    return moment(expirationDate, 'YYYY-MM').format(DateFormatConstants.CARD_EXPIRATION);
  }

  /**
   * Test whether a payment account expiration date will be expired by a given date.
   *
   * @param paymentAccount
   * @param onDate
   */
  isPaymentAccountExpired(paymentAccount: PaymentAccount, onDate: Date = new Date()): boolean {
    const expirationMoment = moment(paymentAccount.cardExpiration, 'YYYY-MM').endOf('month');
    return moment(onDate).isAfter(expirationMoment);
  }

  /**
   * Adds or replaces a payment account in a list
   *
   * @param replacementPaymentAccount
   * @param paymentAccountList
   */
  addOrReplacePaymentAccountInList(replacementPaymentAccount: PaymentAccount, paymentAccountList: Array<PaymentAccount>) {
    if (isNullOrUndefined(paymentAccountList) || isNullOrUndefined(replacementPaymentAccount)) {
      return paymentAccountList;
    }

    const existingIndex = paymentAccountList.findIndex(pa => pa.id === replacementPaymentAccount.id);

    if (existingIndex === -1) {
      paymentAccountList.push(replacementPaymentAccount);
    } else {
      paymentAccountList.splice(existingIndex, 1, replacementPaymentAccount);
    }

    return paymentAccountList;
  }
}
